<template>
  <div class="stlBrowserWrap">
    <div v-if="showFilters && filterGroups.length" class="stlBrowserFilters">
      <div ref="filterBar" class="stlBrowserFiltersDesktop">
        <div class="stlBrowserFilterGroups">
          <shop-the-look-filter-group
            v-for="group in filterGroups"
            :key="group.type"
            :group="group"
            :selected-tag-ids="activeTagIds"
            :is-open="openFilterType === group.type"
            @toggle="toggleTag"
            @toggle-open="toggleFilterGroup"
          />
        </div>

        <div class="stlBrowserFilterSummary">
          <div v-if="activeFilterChips.length" class="stlActiveFilters">
            <button
              v-for="chip in activeFilterChips"
              :key="chip.id"
              type="button"
              class="stlActiveFilterChip"
              @click="toggleTag(chip.id)"
            >
              <span>{{ chip.value }}</span>
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <button
            type="button"
            class="stlFilterReset"
            :disabled="!activeFilterCount"
            @click="clearAllFilters"
          >
            Filter zurücksetzen
          </button>
        </div>
      </div>

      <div class="stlBrowserFiltersMobile">
        <button type="button" class="stlMobileFilterToggle" @click="openMobileFilters">
          <span>Filter öffnen</span>
          <span v-if="activeFilterCount" class="stlMobileFilterCount">{{ activeFilterCount }}</span>
        </button>

        <div v-if="activeFilterChips.length" class="stlMobileActiveFilters">
          <button
            v-for="chip in activeFilterChips"
            :key="chip.id"
            type="button"
            class="stlActiveFilterChip"
            @click="toggleTag(chip.id)"
          >
            <span>{{ chip.value }}</span>
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <button
          v-if="activeFilterCount"
          type="button"
          class="stlFilterReset stlFilterResetMobile"
          @click="clearAllFilters"
        >
          Filter zurücksetzen
        </button>
      </div>
    </div>

    <div v-if="showFilters && isMobileFiltersOpen" class="stlFilterSheetBackdrop" @click.self="closeMobileFilters">
      <div class="stlFilterSheet">
        <div class="stlFilterSheetHeader">
          <div>
            <p class="stlFilterSheetEyebrow">Filter</p>
            <h2>Looks filtern</h2>
          </div>

          <button type="button" class="stlFilterSheetClose" aria-label="Filter schliessen" @click="closeMobileFilters">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <div class="stlFilterSheetMeta">
          <div v-if="activeFilterChips.length" class="stlActiveFilters stlActiveFiltersSheet">
            <button
              v-for="chip in activeFilterChips"
              :key="chip.id"
              type="button"
              class="stlActiveFilterChip"
              @click="toggleTag(chip.id)"
            >
              <span>{{ chip.value }}</span>
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <button
            type="button"
            class="stlFilterReset"
            :disabled="!activeFilterCount"
            @click="clearAllFilters"
          >
            Filter zurücksetzen
          </button>
        </div>

        <div class="stlFilterSheetGroups">
          <shop-the-look-filter-group
            v-for="group in filterGroups"
            :key="`mobile-${group.type}`"
            :group="group"
            :selected-tag-ids="activeTagIds"
            :is-open="openFilterType === group.type"
            :is-mobile="true"
            @toggle="toggleTag"
            @toggle-open="toggleFilterGroup"
          />
        </div>
      </div>
    </div>

    <div ref="grid" class="stlBrowserGrid" :class="{ 'is-masonry-ready': isMasonryReady }">
      <div v-if="isFilterLoading" class="stlBrowserGridLoading" aria-hidden="true">
        <div class="stlBrowserGridLoadingSpinner" />
      </div>
      <div class="stlBrowserSizer" />
      <div v-for="(look, index) in looks" :key="look.id" class="stlBrowserItem">
        <shop-the-look-stack
          ref="lookStacks"
          :group="look"
          :auto-cycle="false"
          @open-fullscreen="openFullscreen(index, $event)"
          @layout-needed="scheduleLayout"
        />
      </div>
    </div>

    <div v-if="error" class="stlBrowserError">
      {{ error }}
    </div>

    <div v-if="hasMore" class="stlBrowserActions">
      <button type="button" class="stlBrowserLoadMore btn btn-bkm" :disabled="isLoading" @click="loadMore">
        {{ isLoading ? 'Lade...' : 'Mehr laden' }}
      </button>
    </div>

    <shop-the-look-fullscreen
      v-if="isFullscreenOpen && activeLook"
      :look="activeLook"
      :selected-look-id="activeVariantId"
      :is-loading="isLoading"
      :is-prev-disabled="isFirstLookActive"
      :is-next-disabled="isLastLookActive"
      @close="closeFullscreen"
      @request-next="showNextLook"
      @request-previous="showPreviousLook"
      @select-variant="setActiveVariantId"
      @select-tag="handleFullscreenTagSelect"
    />
  </div>
</template>

<script>
import ShopTheLookStack from './ShopTheLookStack.vue'
import ShopTheLookFilterGroup from './ShopTheLookFilterGroup.vue'
import ShopTheLookFullscreen from './ShopTheLookFullscreen.vue'

let Masonry = null
let imagesLoaded = null

export default {
  name: 'ShopTheLookBrowser',
  components: {
    ShopTheLookStack,
    ShopTheLookFilterGroup,
    ShopTheLookFullscreen
  },
  props: {
    initialData: {
      type: Object,
      default () {
        return {}
      }
    },
    itemsPerPage: {
      type: Number,
      default: 5
    },
    selectedTagIds: {
      type: Array,
      default () {
        return []
      }
    },
    endpoint: {
      type: String,
      default: '/rest/shopthelook/storefront/looks'
    },
    showFilters: {
      type: Boolean,
      default: true
    },
    syncUrl: {
      type: Boolean,
      default: true
    }
  },
  data () {
    const initialFilters = this.initialData.filters || {}
    const initialTagIds = Array.isArray(initialFilters.tagIds) && initialFilters.tagIds.length
      ? initialFilters.tagIds.map(tagId => Number(tagId)).filter(tagId => tagId > 0)
      : this.selectedTagIds.map(tagId => Number(tagId)).filter(tagId => tagId > 0)

    return {
      looks: this.initialData.data || [],
      meta: this.initialData.meta || {},
      activeTagIds: initialTagIds,
      filterGroups: Array.isArray(initialFilters.availableTagGroups) ? initialFilters.availableTagGroups : [],
      baseItemsPerPage: Math.max(1, Number(this.itemsPerPage) || 5),
      nextOffset: this.initialData.meta ? this.initialData.meta.nextOffset : 0,
      isLoading: false,
      isFilterLoading: false,
      error: null,
      openFilterType: null,
      isMobileFiltersOpen: false,
      isFullscreenOpen: false,
      activeLookIndex: -1,
      activeVariantId: 0,
      masonry: null,
      isMasonryReady: false,
      layoutFrame: null,
      filterTimer: null,
      latestRequestId: 0,
      latestTagIdsKey: initialTagIds.join(','),
      stackCycleTimer: null,
      stackCycleIndex: -1,
      isStackCycleRunning: false
    }
  },
  computed: {
    hasMore () {
      return this.nextOffset !== null && this.nextOffset !== undefined
    },
    activeLook () {
      if (this.activeLookIndex < 0 || this.activeLookIndex >= this.looks.length) {
        return null
      }

      return this.looks[this.activeLookIndex]
    },
    activeFilterChips () {
      const chips = []

      for (let groupIndex = 0; groupIndex < this.filterGroups.length; groupIndex++) {
        const group = this.filterGroups[groupIndex]
        const tags = Array.isArray(group.tags) ? group.tags : []

        for (let tagIndex = 0; tagIndex < tags.length; tagIndex++) {
          const tag = tags[tagIndex]
          const normalizedTagId = Number(tag.id)

          if (this.activeTagIds.indexOf(normalizedTagId) >= 0) {
            chips.push({
              id: normalizedTagId,
              value: tag.value
            })
          }
        }
      }

      return chips
    },
    activeFilterCount () {
      return this.activeFilterChips.length
    },
    isFirstLookActive () {
      return this.activeLookIndex <= 0
    },
    isLastLookActive () {
      if (!this.looks.length) {
        return true
      }

      return this.activeLookIndex >= this.looks.length - 1 && !this.hasMore
    }
  },
  watch: {
    looks () {
      this.scheduleLayout()

      this.$nextTick(() => {
        this.stackCycleIndex = -1
        this.scheduleStackCycle()
      })
    }
  },
  async mounted () {
    if (typeof window === 'undefined') {
      return
    }

    this.initializeMasonry()
    await this.restoreFromLocation()
    window.addEventListener('resize', this.scheduleLayout)
    if (this.syncUrl) {
      window.addEventListener('popstate', this.onPopState)
    }
    document.addEventListener('click', this.onDocumentClick)
    window.addEventListener('keydown', this.onWindowKeydown)
    this.scheduleStackCycle()
  },
  beforeDestroy () {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.scheduleLayout)
      window.removeEventListener('popstate', this.onPopState)
      window.removeEventListener('keydown', this.onWindowKeydown)
    }

    if (typeof document !== 'undefined') {
      document.removeEventListener('click', this.onDocumentClick)
      document.body.classList.remove('stlFiltersOpen')
    }

    if (this.layoutFrame) {
      window.cancelAnimationFrame(this.layoutFrame)
      this.layoutFrame = null
    }

    if (this.filterTimer) {
      clearTimeout(this.filterTimer)
      this.filterTimer = null
    }

    this.clearStackCycle()

    if (this.masonry) {
      this.masonry.destroy()
      this.masonry = null
    }
  },
  methods: {
    onDocumentClick (event) {
      if (!event || this.isMobileFiltersOpen || !this.openFilterType || !this.$refs.filterBar) {
        return
      }

      if (!this.$refs.filterBar.contains(event.target)) {
        this.openFilterType = null
      }
    },
    onWindowKeydown (event) {
      if (!event || event.key !== 'Escape') {
        return
      }

      if (this.isMobileFiltersOpen) {
        this.closeMobileFilters()
        return
      }

      this.openFilterType = null
    },
    onPopState () {
      this.closeMobileFilters()
      this.closeFullscreen()
      this.restoreFromLocation()
    },
    toggleFilterGroup (groupType) {
      this.openFilterType = this.openFilterType === groupType ? null : groupType
    },
    openMobileFilters () {
      this.isMobileFiltersOpen = true
      document.body.classList.add('stlFiltersOpen')
    },
    closeMobileFilters () {
      this.isMobileFiltersOpen = false
      this.openFilterType = null

      if (typeof document !== 'undefined') {
        document.body.classList.remove('stlFiltersOpen')
      }
    },
    openFullscreen (index, selectedLookId) {
      if (index < 0 || index >= this.looks.length) {
        return
      }

      this.activeLookIndex = index
      this.activeVariantId = this.normalizeSelectedLookId(this.looks[index], selectedLookId)
      this.isFullscreenOpen = true
      this.clearStackCycle()
    },
    closeFullscreen () {
      this.isFullscreenOpen = false
      this.activeLookIndex = -1
      this.activeVariantId = 0
      this.scheduleStackCycle()
    },
    setActiveVariantId (lookId) {
      this.activeVariantId = Number(lookId) || 0
    },
    buildLookVariants (look) {
      const variants = []
      const seenIds = {}

      const addLook = (variant) => {
        const id = Number(variant && variant.id)
        if (!variant || id <= 0 || seenIds[id]) {
          return
        }

        seenIds[id] = true
        variants.push(variant)
      }

      addLook(look)

      if (look && Array.isArray(look.children)) {
        look.children.forEach(addLook)
      }

      return variants
    },
    normalizeSelectedLookId (look, selectedLookId) {
      const variants = this.buildLookVariants(look)
      const requestedLookId = Number(selectedLookId)

      if (requestedLookId > 0 && variants.some(variant => Number(variant.id) === requestedLookId)) {
        return requestedLookId
      }

      const backendSelectedLookId = Number(look && look.selectedLookId)
      if (backendSelectedLookId > 0 && variants.some(variant => Number(variant.id) === backendSelectedLookId)) {
        return backendSelectedLookId
      }

      const matchedLookIds = Array.isArray(look && look.matchedLookIds) ? look.matchedLookIds : []
      for (let i = 0; i < matchedLookIds.length; i++) {
        const matchedLookId = Number(matchedLookIds[i])
        if (matchedLookId > 0 && variants.some(variant => Number(variant.id) === matchedLookId)) {
          return matchedLookId
        }
      }

      return variants.length ? Number(variants[0].id) : 0
    },
    async showNextLook () {
      if (!this.isFullscreenOpen) {
        return
      }

      if (this.activeLookIndex < this.looks.length - 1) {
        this.activeLookIndex += 1
        this.activeVariantId = this.normalizeSelectedLookId(this.looks[this.activeLookIndex])
        return
      }

      if (!this.hasMore || this.isLoading) {
        return
      }

      const previousLength = this.looks.length
      await this.loadMore()

      if (this.looks.length > previousLength) {
        this.activeLookIndex = previousLength
        this.activeVariantId = this.normalizeSelectedLookId(this.looks[this.activeLookIndex])
      }
    },
    showPreviousLook () {
      if (!this.isFullscreenOpen || this.activeLookIndex <= 0) {
        return
      }

      this.activeLookIndex -= 1
      this.activeVariantId = this.normalizeSelectedLookId(this.looks[this.activeLookIndex])
    },
    initializeMasonry () {
      if (!this.$refs.grid) {
        return false
      }

      if (!Masonry) {
        Masonry = require('masonry-layout')
        Masonry = Masonry.default || Masonry
      }

      if (!imagesLoaded) {
        imagesLoaded = require('imagesloaded')
        imagesLoaded = imagesLoaded.default || imagesLoaded
      }

      this.$nextTick(() => {
        if (!this.$refs.grid) {
          return
        }

        if (!this.masonry) {
          this.masonry = new Masonry(this.$refs.grid, {
            itemSelector: '.stlBrowserItem',
            columnWidth: '.stlBrowserSizer',
            percentPosition: true,
            horizontalOrder: false,
            transitionDuration: 0
          })
        }

        this.refreshMasonry({ markReady: true, waitForImages: true })
      })

      return true
    },
    refreshMasonry ({ markReady = false, waitForImages = false } = {}) {
      if (!this.masonry || !this.$refs.grid) {
        return
      }

      this.$nextTick(() => {
        if (!this.masonry || !this.$refs.grid) {
          return
        }

        const applyLayout = () => {
          if (!this.masonry) {
            return
          }

          this.masonry.reloadItems()
          this.masonry.layout()

          if (markReady && !this.isMasonryReady) {
            this.isMasonryReady = true
          }
        }

        applyLayout()

        if (waitForImages) {
          imagesLoaded(this.$refs.grid, applyLayout)
        }
      })
    },
    scheduleLayout (waitForImages = true) {
      if (typeof window === 'undefined') {
        return
      }

      const shouldWaitForImages = waitForImages !== false

      if (this.layoutFrame) {
        window.cancelAnimationFrame(this.layoutFrame)
      }

      this.layoutFrame = window.requestAnimationFrame(() => {
        this.layoutFrame = null

        if (!this.masonry && !this.initializeMasonry()) {
          return
        }

        this.refreshMasonry({ waitForImages: shouldWaitForImages })
      })
    },
    stackComponentRefs () {
      if (!this.$refs.lookStacks) {
        return []
      }

      return Array.isArray(this.$refs.lookStacks) ? this.$refs.lookStacks : [this.$refs.lookStacks]
    },
    isStackVisible (stackComponent) {
      if (typeof window === 'undefined' || !stackComponent || !stackComponent.$el) {
        return false
      }

      const rect = stackComponent.$el.getBoundingClientRect()
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0

      if (rect.width <= 0 || rect.height <= 0 || rect.bottom <= 0 || rect.right <= 0 || rect.top >= viewportHeight || rect.left >= viewportWidth) {
        return false
      }

      const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0)
      return visibleHeight >= Math.min(rect.height * 0.4, 220)
    },
    canRunStackCycle () {
      if (typeof window === 'undefined') {
        return false
      }

      if (typeof document !== 'undefined' && document.hidden) {
        return false
      }

      return !this.isFullscreenOpen && !this.isLoading && !this.isFilterLoading && this.activeTagIds.length === 0 && this.looks.length > 0
    },
    clearStackCycle () {
      if (this.stackCycleTimer) {
        window.clearTimeout(this.stackCycleTimer)
        this.stackCycleTimer = null
      }
    },
    scheduleStackCycle (delay = 6500) {
      this.clearStackCycle()

      if (typeof window === 'undefined') {
        return
      }

      this.stackCycleTimer = window.setTimeout(this.runStackCycle, delay)
    },
    findNextVisibleStack () {
      const stackRefs = this.stackComponentRefs()

      for (let offset = 1; offset <= stackRefs.length; offset++) {
        const index = (this.stackCycleIndex + offset) % stackRefs.length
        const stackComponent = stackRefs[index]

        if (
          stackComponent &&
          typeof stackComponent.canBeTriggeredCycle === 'function' &&
          stackComponent.canBeTriggeredCycle() &&
          this.isStackVisible(stackComponent)
        ) {
          this.stackCycleIndex = index
          return stackComponent
        }
      }

      return null
    },
    async runStackCycle () {
      this.stackCycleTimer = null

      if (this.isStackCycleRunning || !this.canRunStackCycle()) {
        this.scheduleStackCycle(3000)
        return
      }

      const stackComponent = this.findNextVisibleStack()
      if (stackComponent && typeof stackComponent.cycleToNextVariant === 'function') {
        this.isStackCycleRunning = true
        try {
          await stackComponent.cycleToNextVariant(true)
        } finally {
          this.isStackCycleRunning = false
        }

        this.scheduleStackCycle()
        return
      }

      this.scheduleStackCycle()
    },
    parseTagIds (value) {
      if (!value) {
        return []
      }

      return value
        .split(',')
        .map(tagId => Number(tagId))
        .filter(tagId => tagId > 0)
    },
    buildTagIdsKey (tagIds) {
      return tagIds.join(',')
    },
    haveTagIdsChanged (selectedTagIds) {
      if (selectedTagIds.length !== this.activeTagIds.length) {
        return true
      }

      for (let i = 0; i < selectedTagIds.length; i++) {
        if (selectedTagIds[i] !== this.activeTagIds[i]) {
          return true
        }
      }

      return false
    },
    async restoreFromLocation () {
      if (!this.syncUrl || typeof window === 'undefined') {
        return
      }

      const searchParams = new window.URLSearchParams(window.location.search)
      const selectedTagIds = this.parseTagIds(searchParams.get('tagIds'))
      const baseItemsPerPage = Math.max(1, Number(searchParams.get('items')) || this.baseItemsPerPage || 5)
      const loadedCount = Math.max(baseItemsPerPage, Number(searchParams.get('loaded')) || baseItemsPerPage)

      const shouldReload = loadedCount !== this.looks.length || this.haveTagIdsChanged(selectedTagIds)
      if (!shouldReload) {
        return
      }

      this.baseItemsPerPage = baseItemsPerPage
      this.activeTagIds = selectedTagIds.slice()
      this.latestTagIdsKey = this.buildTagIdsKey(selectedTagIds)

      await this.fetchLooks({
        itemsPerPage: loadedCount,
        offset: 0,
        append: false,
        selectedTagIds,
        updateHistory: false,
        usePushState: false,
        markAsFilterRequest: false
      })
    },
    updateUrl (usePushState) {
      if (!this.syncUrl || typeof window === 'undefined') {
        return
      }

      const nextUrl = new window.URL(window.location.href)
      const searchParams = new window.URLSearchParams()

      if (this.activeTagIds.length) {
        searchParams.set('tagIds', this.activeTagIds.join(','))
      }

      searchParams.set('items', String(this.baseItemsPerPage))
      searchParams.set('loaded', String(this.looks.length))
      nextUrl.search = searchParams.toString()

      if (usePushState) {
        window.history.pushState({}, '', nextUrl.toString())
        return
      }

      window.history.replaceState({}, '', nextUrl.toString())
    },
    async fetchLooks ({ itemsPerPage, offset, append, selectedTagIds, updateHistory, usePushState, markAsFilterRequest }) {
      const requestId = ++this.latestRequestId

      this.isLoading = true
      this.clearStackCycle()

      if (markAsFilterRequest) {
        this.isFilterLoading = true
      }

      this.error = null

      try {
        const requestTagIdsKey = this.buildTagIdsKey(selectedTagIds)
        const searchParams = new window.URLSearchParams()
        searchParams.set('itemsPerPage', String(itemsPerPage))
        searchParams.set('offset', String(offset))

        if (selectedTagIds.length) {
          searchParams.set('tagIds', selectedTagIds.join(','))
        }

        const response = await window.fetch(`${this.endpoint}?${searchParams.toString()}`, {
          credentials: 'same-origin'
        })

        if (!response.ok) {
          throw new Error('Shop the look request failed')
        }

        const result = await response.json()
        if (requestId !== this.latestRequestId) {
          return
        }

        if (requestTagIdsKey !== this.latestTagIdsKey) {
          return
        }

        if (!append && this.masonry) {
          this.masonry.destroy()
          this.masonry = null
          this.isMasonryReady = false
        }

        this.looks = append ? this.looks.concat(result.data || []) : (result.data || [])
        this.meta = result.meta || {}
        this.filterGroups = (((result || {}).filters || {}).availableTagGroups) || this.filterGroups
        this.nextOffset = this.meta.nextOffset !== undefined ? this.meta.nextOffset : null

        if (this.openFilterType) {
          const hasOpenGroup = this.filterGroups.some(group => group.type === this.openFilterType)
          if (!hasOpenGroup) {
            this.openFilterType = null
          }
        }

        if (this.isFullscreenOpen && this.activeLookIndex >= this.looks.length) {
          this.activeLookIndex = this.looks.length ? this.looks.length - 1 : -1
        }

        if (this.isFullscreenOpen && this.activeLookIndex >= 0) {
          this.activeVariantId = this.normalizeSelectedLookId(this.looks[this.activeLookIndex], this.activeVariantId)
        }

        if (updateHistory) {
          this.updateUrl(usePushState)
        }

        if (markAsFilterRequest && !append) {
          this.$nextTick(this.scrollToBrowserTop)
        }
      } catch (error) {
        if (requestId !== this.latestRequestId) {
          return
        }

        if (this.buildTagIdsKey(selectedTagIds) !== this.latestTagIdsKey) {
          return
        }

        this.error = 'Shop the look konnte nicht geladen werden.'
      } finally {
        if (requestId === this.latestRequestId && this.buildTagIdsKey(selectedTagIds) === this.latestTagIdsKey) {
          this.isLoading = false
          this.isFilterLoading = false
          this.$nextTick(() => {
            this.scheduleStackCycle()
          })
        }
      }
    },
    async loadMore () {
      if (this.isLoading || !this.hasMore) {
        return
      }

      await this.fetchLooks({
        itemsPerPage: this.baseItemsPerPage,
        offset: Number(this.nextOffset || 0),
        append: true,
        selectedTagIds: this.activeTagIds,
        updateHistory: true,
        usePushState: false,
        markAsFilterRequest: false
      })
    },
    scheduleFilterFetch () {
      if (this.filterTimer) {
        clearTimeout(this.filterTimer)
      }

      this.updateUrl(true)

      this.filterTimer = setTimeout(() => {
        this.filterTimer = null

        this.fetchLooks({
          itemsPerPage: this.baseItemsPerPage,
          offset: 0,
          append: false,
          selectedTagIds: this.activeTagIds.slice(),
          updateHistory: true,
          usePushState: true,
          markAsFilterRequest: true
        })
      }, 250)
    },
    scrollToBrowserTop () {
      if (typeof window === 'undefined' || !this.$el || typeof this.$el.getBoundingClientRect !== 'function') {
        return
      }

      const targetTop = Math.max(0, this.$el.getBoundingClientRect().top + window.pageYOffset - 20)

      window.scrollTo({
        top: targetTop,
        behavior: 'smooth'
      })
    },
    toggleTag (tagId) {
      if (!this.showFilters) {
        return
      }

      const normalizedTagId = Number(tagId)
      if (normalizedTagId <= 0) {
        return
      }

      const activeTagIds = this.activeTagIds.slice()
      const currentIndex = activeTagIds.indexOf(normalizedTagId)

      if (currentIndex >= 0) {
        activeTagIds.splice(currentIndex, 1)
      } else {
        activeTagIds.push(normalizedTagId)
      }

      activeTagIds.sort((left, right) => left - right)
      this.activeTagIds = activeTagIds
      this.latestTagIdsKey = this.buildTagIdsKey(activeTagIds)
      this.isFilterLoading = true
      this.closeFullscreen()
      this.scheduleFilterFetch()
    },
    handleFullscreenTagSelect (tagId) {
      if (!this.showFilters) {
        return
      }

      this.applySingleTagFilter(tagId)
    },
    applySingleTagFilter (tagId) {
      if (!this.showFilters) {
        return
      }

      const normalizedTagId = Number(tagId)
      if (normalizedTagId <= 0) {
        return
      }

      this.activeTagIds = [normalizedTagId]
      this.latestTagIdsKey = String(normalizedTagId)
      this.isFilterLoading = true
      this.closeFullscreen()
      this.scheduleFilterFetch()
    },
    clearAllFilters () {
      if (!this.showFilters) {
        return
      }

      if (!this.activeTagIds.length) {
        return
      }

      this.activeTagIds = []
      this.latestTagIdsKey = ''
      this.isFilterLoading = true
      this.closeFullscreen()
      this.scheduleFilterFetch()
    }
  }
}
</script>
